import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

function Sk({ className, ...props }) {
  return <Skeleton className={cn("bg-[#E2E8F0]", className)} {...props} />;
}

export function AuthLoadingSkeleton() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8F9FA]" data-testid="auth-loading">
      <div className="w-full max-w-xs space-y-4 px-6">
        <Sk className="h-12 w-12 rounded-xl mx-auto" />
        <Sk className="h-4 w-40 mx-auto" />
        <Sk className="h-3 w-28 mx-auto" />
      </div>
    </div>
  );
}

export function FilterPillsSkeleton({ count = 5, className }) {
  return (
    <div className={cn("flex flex-wrap gap-2", className)}>
      {Array.from({ length: count }).map((_, i) => (
        <Sk key={i} className="h-9 w-24 rounded-full" />
      ))}
    </div>
  );
}

export function CardGridSkeleton({ count = 6, className, testId }) {
  return (
    <div className={cn("grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4", className)} data-testid={testId}>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="bg-white border border-[#E2E8F0] rounded-lg p-6 space-y-4">
          <div className="flex items-center justify-between">
            <Sk className="h-10 w-10 rounded-lg" />
            <Sk className="h-4 w-4 rounded" />
          </div>
          <Sk className="h-3 w-20" />
          <Sk className="h-6 w-3/4" />
          <Sk className="h-4 w-1/2" />
          <div className="pt-4 border-t border-[#E2E8F0] space-y-2">
            <Sk className="h-3 w-full" />
            <Sk className="h-1.5 w-full rounded-full" />
          </div>
        </div>
      ))}
    </div>
  );
}

export function TableRowsSkeleton({ rows = 8, testId }) {
  return (
    <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden" data-testid={testId}>
      <div className="grid grid-cols-12 gap-4 px-6 py-3 border-b border-[#E2E8F0] bg-[#F8FAFC]">
        <Sk className="col-span-4 h-3" />
        <Sk className="col-span-3 h-3 hidden md:block" />
        <Sk className="col-span-2 h-3" />
        <Sk className="col-span-3 h-3" />
      </div>
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="grid grid-cols-12 gap-4 items-center px-6 py-4 border-b border-[#E2E8F0] last:border-b-0">
          <div className="col-span-5 md:col-span-4 flex items-center gap-3">
            <Sk className="h-9 w-9 rounded-full shrink-0" />
            <div className="flex-1 space-y-2">
              <Sk className="h-4 w-32" />
              <Sk className="h-3 w-20" />
            </div>
          </div>
          <Sk className="hidden md:block md:col-span-3 h-4" />
          <Sk className="col-span-3 md:col-span-2 h-4" />
          <Sk className="col-span-4 md:col-span-3 h-4 ml-auto w-16" />
        </div>
      ))}
    </div>
  );
}

export function TimetableGridSkeleton({ testId = "timetable-loading" }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid={testId}>
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="bg-white border border-[#E2E8F0] rounded-lg p-6 space-y-4">
          <div className="flex justify-between">
            <Sk className="h-6 w-28 rounded" />
            <Sk className="h-3 w-8" />
          </div>
          <Sk className="h-6 w-4/5" />
          <Sk className="h-3 w-24" />
          <div className="pt-4 border-t border-[#E2E8F0] flex justify-between">
            <Sk className="h-4 w-24" />
            <Sk className="h-4 w-20" />
          </div>
          <Sk className="h-10 w-full rounded-md" />
        </div>
      ))}
    </div>
  );
}

export function SessionsListSkeleton({ testId = "sessions-loading" }) {
  return (
    <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden" data-testid={testId}>
      {Array.from({ length: 7 }).map((_, i) => (
        <div key={i} className="px-6 py-5 border-b border-[#E2E8F0] last:border-b-0 flex items-center gap-4">
          <Sk className="h-10 w-10 rounded-lg shrink-0" />
          <div className="flex-1 space-y-2">
            <Sk className="h-4 w-48" />
            <Sk className="h-3 w-64" />
          </div>
          <Sk className="h-8 w-20 rounded-md hidden sm:block" />
        </div>
      ))}
    </div>
  );
}

export function DetailPageSkeleton({ testId, statCount = 3, tableRows = 8 }) {
  return (
    <div className="min-h-screen" data-testid={testId}>
      <div className="px-6 md:px-12 pt-10 pb-6 space-y-4">
        <Sk className="h-4 w-16" />
        <Sk className="h-3 w-40" />
        <Sk className="h-12 w-2/3 max-w-lg" />
      </div>
      <div className={cn("grid border-t border-l border-[#E2E8F0] bg-white", statCount === 3 ? "grid-cols-3" : "grid-cols-2 lg:grid-cols-4")}>
        {Array.from({ length: statCount }).map((_, i) => (
          <div key={i} className="border-r border-b border-[#E2E8F0] p-6 space-y-3">
            <Sk className="h-3 w-20" />
            <Sk className="h-9 w-16" />
          </div>
        ))}
      </div>
      <div className="px-6 md:px-12 py-8">
        <Sk className="h-7 w-40 mb-6" />
        <TableRowsSkeleton rows={tableRows} />
      </div>
    </div>
  );
}

export function SessionPageSkeleton({ testId = "session-loading" }) {
  return (
    <div className="min-h-screen" data-testid={testId}>
      <div className="px-6 md:px-12 pt-10 pb-6 space-y-4">
        <Sk className="h-4 w-16" />
        <div className="flex gap-2">
          <Sk className="h-6 w-24 rounded" />
          <Sk className="h-6 w-32 rounded" />
        </div>
        <Sk className="h-12 w-2/3 max-w-md" />
        <Sk className="h-4 w-48" />
      </div>
      <div className="px-6 md:px-12 pb-6">
        <div className="bg-white border border-[#E2E8F0] rounded-lg p-4 flex justify-between gap-4">
          <Sk className="h-8 w-40" />
          <div className="flex gap-2">
            <Sk className="h-9 w-24 rounded-md" />
            <Sk className="h-9 w-24 rounded-md" />
            <Sk className="h-9 w-20 rounded-md" />
          </div>
        </div>
      </div>
      <div className="px-6 md:px-12 pb-16">
        <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden">
          {Array.from({ length: 10 }).map((_, i) => (
            <div key={i} className="px-6 py-4 flex items-center gap-4 border-b border-[#E2E8F0] last:border-b-0">
              <Sk className="h-10 w-10 rounded-full shrink-0" />
              <div className="flex-1 space-y-2">
                <Sk className="h-4 w-36" />
                <Sk className="h-3 w-20" />
              </div>
              <div className="flex gap-2">
                <Sk className="h-8 w-20 rounded-md" />
                <Sk className="h-8 w-20 rounded-md" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function DashboardSkeleton({ testId = "dashboard-loading" }) {
  return (
    <div className="px-6 md:px-12 pb-16" data-testid={testId}>
      <div className="border-t border-l border-[#E2E8F0]">
        <div className="grid grid-cols-2 lg:grid-cols-4 bg-white">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="border-r border-b border-[#E2E8F0] p-6 space-y-3">
              <Sk className="h-3 w-24" />
              <Sk className="h-9 w-16" />
              <Sk className="h-3 w-32" />
            </div>
          ))}
        </div>
      </div>
      <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="bg-white border border-[#E2E8F0] rounded-lg p-5 space-y-3">
            <Sk className="h-3 w-20" />
            <Sk className="h-9 w-14" />
            <Sk className="h-4 w-28" />
            <Sk className="h-2 w-full rounded-full" />
          </div>
        ))}
      </div>
      <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-[#E2E8F0] rounded-lg p-6">
          <Sk className="h-4 w-40 mb-6" />
          <Sk className="h-64 w-full rounded-lg" />
        </div>
        <div className="bg-white border border-[#E2E8F0] rounded-lg p-6">
          <Sk className="h-4 w-40 mb-6" />
          <Sk className="h-64 w-full rounded-lg" />
        </div>
      </div>
    </div>
  );
}

export function TimetableAdminSkeleton({ testId = "timetable-admin-loading" }) {
  return (
    <div className="px-6 md:px-12 py-8 space-y-6" data-testid={testId}>
      <FilterPillsSkeleton count={6} />
      <FilterPillsSkeleton count={7} />
      <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden">
        {Array.from({ length: 7 }).map((_, i) => (
          <div key={i} className="grid grid-cols-5 gap-4 px-4 py-4 border-b border-[#E2E8F0] last:border-b-0">
            <Sk className="h-10 w-24" />
            <Sk className="h-10 col-span-2" />
            <Sk className="h-10 w-28" />
            <Sk className="h-8 w-20 ml-auto" />
          </div>
        ))}
      </div>
    </div>
  );
}
